import { Component, Event, EventEmitter, Fragment, h, Method, Prop, State } from '@stencil/core';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { InputAttr } from './vs-input.model';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';
import { CONFIRMATION_FIELD } from '@src/components/vs-form-builder/helpers/constant';


@Component({
  tag: 'vs-input',
  shadow: false,
})

export class VsInput {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription If true, show textarea instead of input box.
   * @controlName isTextArea */
  @Prop() isTextArea: boolean = false;

  /**
   * @shortDescription Index of input field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription Define the label for input field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription Label exists in the input field as eyebrow view.
   * @controlName eyebrow */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription Input's type
   * @controlName type */
  @Prop() type: string = 'text';

  /**
   * @shortDescription Input's placeholder
   * @controlName placeholder */
  @Prop() placeholder: string;

  /**
   * @shortDescription Validators rules to check against for the input.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   @shortDescription is date input
   @controlName isDate
   */
  @Prop() isDate: boolean;

  /**
   @shortDescription date format
   @controlName dateFormat
   */
  @Prop() dateFormat = 'DD-MM-YYYY';

  @Prop() defaultValue;

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;
  /* END GENAI@GHCOPILOT */

  @Prop() inputPrefix: any[];

  @State() showError: boolean = false;
  @State() value: string = '';
  @State() selectedErrorMessage: string = '';
  @State() valid: boolean = true;
  @State() inputAllyAlert: string = 'input_alert_';
  @State() isRequired: boolean;
  @State() maxLength: number;

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  private inputEl?;

  private prefixEl?;

  /**
   * @shortDescription Emit input value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    this.value = this.defaultValue;
    this.updateInput(this.value);
    this.showError = isShowError;
  }

  /**
   * @shortDescription set input focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.showError = true;
    this.inputEl.focus();
  }

  componentWillLoad() {
    this.value = this.defaultValue;
    this.inputAllyAlert += this.inputId;
    this.isRequired = this.validators.findIndex(validator => validator.key === VALIDATION_ATTRIBUTES.REQUIRE) > -1;
    this.maxLength = this.validators.find(validator => validator.key === VALIDATION_ATTRIBUTES.MAX)?.config;
    this.updateInput(this.value);
  }

  private onBlurInput = () => {
    this.showError = true;
    this.blurEvent.emit();
  };

  private validate = (value: string) => {
    const validationAttr = checkValidations(this.validators, value);
    this.selectedErrorMessage = validationAttr.errorMessage;
    this.valid = getValidStateAccordingToRequiredStatus(this.isRequired, value, validationAttr);
  };

  private onInputChange = (event) => {
    const { target } = event;
    this.updateInput(target.value);
  };

  private getDateAriaDescribedBy() {
    return `${this.isDate ? `${this.inputId}Format` : ''}`;
  }

  private getInputAriaDescribedBy() {
    return `${this.inputAllyAlert}${this.inputId} ${this.getDateAriaDescribedBy()}`;
  }
  private updateInput = (value: string) => {
    this.value = value;
    this.validate(this.value);
    this.computedInputValue.emit(
      {
        index: this.index,
        key: this.inputId,
        // START GENAI@GHCOPILOT
       value: this.inputPrefix && this.prefixEl ? this.prefixEl.value + ' ' + this.value : this.value,
        // END GENAI@GHCOPILOT
        isValid: this.valid,
        error: this.selectedErrorMessage,
      } as InputAttr,
    );

  };

  private renderTextAreaField(){
    return (<textarea
      ref={el => this.inputEl = el as HTMLTextAreaElement}
      class={`vs-input ${this.showError && !this.valid ? 'vs-error' : ''} ${this.inputPrefix ?'vs-col-lg-9':''}`}
      id={this.inputId}
      name={this.inputId}
      maxLength={this.maxLength}
      placeholder={this.placeholder}
      value={this.value}
      onInput={this.onInputChange}
      onBlur={this.onBlurInput}
      aria-invalid={this.showError && !this.valid}
      aria-describedby={this.showError && !this.valid
        ? this.getInputAriaDescribedBy() : this.getDateAriaDescribedBy()}
    />);
  }

  private renderInputField() {
    return (
      <input
        ref={el => this.inputEl = el as HTMLInputElement}
        class={`vs-input ${(this.showError && !this.valid) && 'vs-error'} ${this.inputPrefix ?'vs-col-lg-9':''}`}
        id={this.inputId}
        name={this.inputId}
        type={this.type}
        maxLength={this.maxLength}
        placeholder={this.placeholder}
        value={this.value}
        onInput={this.onInputChange}
        onBlur={this.onBlurInput}
        disabled={this.defaultValue?.length > 0}
        aria-invalid={this.showError && !this.valid}
        aria-describedby={this.showError && !this.valid
          ? this.getInputAriaDescribedBy() : this.getDateAriaDescribedBy()}
        {...(this.inputId?.slice(-7) === CONFIRMATION_FIELD && {
          autocomplete: 'off',
          onPaste: (e) => e.preventDefault(),
          onCopy: (e) => e.preventDefault(),
        })}
      />
    );
  }

  render() {
    return (
      <Fragment>
        {this.aboveInputContent && <div class='vs-text vs-mb-0' innerHTML={this.aboveInputContent} />}
        <div class={`${this.eyebrow ? 'vs-has-eyebrow' : ''}`} id={'wrapper_' + this.inputId}>
          <div class={`vs-input-group vs-height-auto vs-mb-1 ${this.eyebrow ? 'vs-eyebrow-group' : ''}`}>
            {this.label && (
              <label class={`vs-label ${this.eyebrow ? 'vs-label-eyebrow' : ''} vs-has-focus`}
                     htmlFor={this.inputId}>
                <span innerHTML={this.label}/>
                {/*{this.isDate && <span id={`${this.inputId}Format`}> ({this.dateFormat}) </span>}*/}
                {this.isRequired ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
              </label>
            )}
            {this.betweenInputContent && <div class='vs-text vs-mb-0' innerHTML={this.betweenInputContent} />}
            <div class='vs-row vs-mx-0'>
              {this.inputPrefix && <select class="vs-input vs-col-lg-3 " ref={el => this.prefixEl = el as HTMLSelectElement}>
                {this.inputPrefix.map(value =>
                  <option value={value}>{value}</option>
                )}
              </select>}
              {this.isTextArea ? this.renderTextAreaField() : this.renderInputField()}
            </div>
            {this.showError && !this.valid && this.selectedErrorMessage && (
              <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
                 id={this.inputAllyAlert + this.inputId}>
                <span aria-hidden='true' class='visa-icon-alert' />
                <span innerHTML={decodeHTMLEntities(this.selectedErrorMessage)} />
              </p>
            )}
          </div>
        </div>
        {this.belowInputContent && <div class='vs-text vs-mb-0' innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  };
}
